import { ChevronDown, ExternalLink } from "lucide-react";
import type { SignedInPerson } from "@shared-core/domain/portal-sign-in/types";
import { Button } from "../primitives/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../primitives/dropdown-menu";

export interface AccountMenuProps {
  person: SignedInPerson;
  /** What a party is called here: "customer" or "supplier". Names *Change customer*. */
  partyNoun: string;
  /** "Open the supplier portal": the shell's words, shown only when the other portal has a party for this person. */
  openOtherPortalLabel: string;
  onAccount(): void;
  onChangeParty(): void;
  onOpenOtherPortal(): void;
  onSignOut(): void;
  /** Start with the menu open (the stories of the menu itself). */
  defaultOpen?: boolean;
}

/**
 * The signed-in person's menu in both portals: named by their label, else their address; *Account*, *Change customer* only with several parties, *Open the
 * supplier portal* only when the other portal has a party for this person, *Sign out*.
 */
export function AccountMenu({
  person,
  partyNoun,
  openOtherPortalLabel,
  onAccount,
  onChangeParty,
  onOpenOtherPortal,
  onSignOut,
  defaultOpen = false,
}: AccountMenuProps) {
  return (
    <DropdownMenu defaultOpen={defaultOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-auto min-w-0 max-w-[min(100%,14rem)] shrink py-1.5 text-left"
          title={`${person.label ?? person.address}, ${person.partyName}`}
        >
          <span className="flex min-w-0 flex-col items-end">
            <span className="max-w-full truncate text-sm font-medium leading-tight">
              {person.label ?? person.address}
            </span>
            <span className="max-w-full truncate text-xs leading-tight text-muted-foreground">
              {person.partyName}
            </span>
          </span>
          <ChevronDown className="size-4 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="space-y-1 font-normal">
          <span className="block text-[11.5px] text-muted-foreground">
            Signed in as
          </span>
          <span className="block truncate text-[13.5px] font-normal">
            {person.address}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onAccount}>Account</DropdownMenuItem>
        {person.hasSeveralParties ? (
          <DropdownMenuItem onSelect={onChangeParty}>
            Change {partyNoun}
          </DropdownMenuItem>
        ) : null}
        {person.otherPortalAvailable ? (
          <DropdownMenuItem onSelect={onOpenOtherPortal}>
            {openOtherPortalLabel}
            <ExternalLink className="ml-auto size-4 text-muted-foreground" />
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem onSelect={onSignOut}>Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
